/**
 * Project Configuration
 * 
 * This file defines all configuration constants for the Task Management app:
 * - Task statuses (used in UI dropdowns and filtering)
 * - Related apps (task categorization)
 * - Task types (bugfix, new feature, feature enhancement)
 * 
 * Using TypeScript's `as const` ensures type safety across the application.
 * Any change here automatically propagates to UI components and database constraints.
 */

// ============================================================================
// TASK STATUSES
// ============================================================================
export const TASK_STATUSES = [
  'new',
  'pending_quote',
  'rejected',
  'rejected_finance_approval',
  'approved',
  'pending_finance_approval',
  'in_dev_progress',
  'released_to_dev',
  'in_dev_testing',
  'in_2nd_dev_testing',
  'on_hold',
  'ready_for_prod',
  'released_to_prod',
  'completed',
] as const;

export type TaskStatus = typeof TASK_STATUSES[number];

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  new: 'New',
  pending_quote: 'Pending Quote',
  rejected: 'Rejected',
  rejected_finance_approval: 'Rejected Finance Approval',
  approved: 'Approved',
  pending_finance_approval: 'Pending Finance Approval',
  in_dev_progress: 'In Dev Progress',
  released_to_dev: 'Released To Dev',
  in_dev_testing: 'In Dev Testing',
  in_2nd_dev_testing: 'In 2nd Dev Testing',
  on_hold: 'On Hold',
  ready_for_prod: 'Ready For Prod',
  released_to_prod: 'Released To Prod',
  completed: 'Completed',
};

export const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  new: '#2563eb',
  pending_quote: '#9333ea',
  rejected: '#dc2626',
  rejected_finance_approval: '#b91c1c',
  approved: '#16a34a',
  pending_finance_approval: '#ca8a04',
  in_dev_progress: '#f59e0b',
  released_to_dev: '#0f766e',
  in_dev_testing: '#7c3aed',
  in_2nd_dev_testing: '#a21caf',
  on_hold: '#6366f1',
  ready_for_prod: '#ea580c',
  released_to_prod: '#0891b2',
  completed: '#475569',
};

// ============================================================================
// RELATED APPS
// ============================================================================
export const RELATED_APPS = [
  'BackEnd',
  'BioMatch.cloud',
  'BioMatch.cloud(Pepkor)',
  'BioVault.cloud',
  'BioVault.cloud(Pepkor)',
  'ClockIQ.cloud',
  'Device firmware',
  'EnrolIQ.cloud',
  'GuestIQ.cloud',
  'IDDM',
  'IQSuite.cloud',
  'IQSuite.cloud Admin',
  'Other',
  'ReportIQ.cloud',
  'TenantIQ.cloud',
  'VisitorIQ.cloud',
] as const;

export type RelatedApp = typeof RELATED_APPS[number];

// ============================================================================
// TASK TYPES
// ============================================================================
export const TASK_TYPES = [
  'bugfix',
  'new_feature',
  'feature_enhancement',
  'new_function_product',
] as const;

export type TaskType = typeof TASK_TYPES[number];

export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  bugfix: 'Bugfix',
  new_feature: 'New Feature',
  feature_enhancement: 'Feature Enhancement',
  new_function_product: 'New Function/Product',
};

export const TASK_TYPE_COLORS: Record<TaskType, string> = {
  bugfix: '#dc2626',
  new_feature: '#0891b2',
  feature_enhancement: '#7c3aed',
  new_function_product: '#0f766e',
};

// ============================================================================
// TASK PRIORITIES
// ============================================================================
export const TASK_PRIORITIES = [
  'low',
  'medium',
  'high',
] as const;

export type TaskPriority = typeof TASK_PRIORITIES[number];

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export const TASK_PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: '#16a34a',
  medium: '#f59e0b',
  high: '#dc2626',
};

// ============================================================================
// GROUPING OPTIONS FOR KANBAN
// ============================================================================
export const KANBAN_GROUP_BY_OPTIONS = [
  'status',
  'assigned_to',
  'type',
  'related_app',
  'date',
] as const;

export type KanbanGroupBy = typeof KANBAN_GROUP_BY_OPTIONS[number];

export const KANBAN_GROUP_BY_LABELS: Record<KanbanGroupBy, string> = {
  status: 'Status',
  assigned_to: 'Assigned To',
  type: 'Task Type',
  related_app: 'Related App',
  date: 'Date',
};

// ============================================================================
// CHART FILTERING
// ============================================================================
// Statuses to EXCLUDE from dashboard charts (not included in aggregations)
export const EXCLUDED_CHART_STATUSES: TaskStatus[] = ['completed'];

// ============================================================================
// PAGINATION & LIMITS
// ============================================================================
export const TASKS_PER_PAGE = 20;
export const MAX_KANBAN_COLUMNS = 10;

// ============================================================================
// VALIDATION RULES
// ============================================================================
export const TASK_VALIDATION = {
  title: {
    minLength: 3,
    maxLength: 255,
  },
  description: {
    maxLength: 5000,
  },
} as const;

// ============================================================================
// EXPORT ALL CONFIGS AS NAMESPACE
// ============================================================================
export const CONFIG = {
  statuses: TASK_STATUSES,
  statusLabels: TASK_STATUS_LABELS,
  statusColors: TASK_STATUS_COLORS,
  apps: RELATED_APPS,
  types: TASK_TYPES,
  typeLabels: TASK_TYPE_LABELS,
  typeColors: TASK_TYPE_COLORS,
  priorities: TASK_PRIORITIES,
  priorityLabels: TASK_PRIORITY_LABELS,
  priorityColors: TASK_PRIORITY_COLORS,
  kanbanGroupBy: KANBAN_GROUP_BY_OPTIONS,
  kanbanGroupByLabels: KANBAN_GROUP_BY_LABELS,
  excludedChartStatuses: EXCLUDED_CHART_STATUSES,
  tasksPerPage: TASKS_PER_PAGE,
  validation: TASK_VALIDATION,
} as const;
